/**
 * Small, allocation-free helpers shared by every scroll scene.
 *
 * Scenes write styles imperatively from one progress value (see SceneTrack)
 * rather than through a forest of `useTransform`s: a scene moves dozens of
 * elements along hand-tuned beat ranges, and keeping each beat as one readable
 * line of arithmetic is what makes the timing tunable at all.
 */

export const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));

/** Local progress of `p` inside the beat [a, b], clamped to 0–1. */
export const range = (p: number, a: number, b: number) => clamp((p - a) / (b - a));

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Close to EASE [0.16, 1, 0.3, 1]: quick departure, long settle. */
export const easeOut = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

/** Close to EASE_IN — for things leaving. */
export const easeIn = (t: number) => t * t * t;

export const smooth = (t: number) => t * t * (3 - 2 * t);

/** Stiff spring with ~10% overshoot: Profile's "snap into the grid". */
export const snap = (t: number) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const s = 2.2;
    return 1 + (s + 1) * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);
};

/** Soft spring with ~18% overshoot: Contact's liquid wobble. */
export const jelly = (t: number) =>
    t <= 0 ? 0 : t >= 1 ? 1 : 1 - Math.exp(-5.2 * t) * Math.cos(9.5 * t);

const GLYPHS = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#%&";
const KEEP = " ·/.-—";

/** Resolves `text` left to right as `t` goes 0 → 1; hidden at 0. */
export function scramble(el: HTMLElement, text: string, t: number) {
    if (t <= 0) {
        el.style.opacity = "0";
        return;
    }
    el.style.opacity = "1";
    if (t >= 1) {
        if (el.textContent !== text) el.textContent = text;
        return;
    }
    const n = Math.floor(text.length * t);
    let s = text.slice(0, n);
    for (let i = n; i < text.length; i++) {
        const ch = text[i];
        s += KEEP.indexOf(ch) >= 0 ? ch : GLYPHS[(Math.random() * GLYPHS.length) | 0];
    }
    el.textContent = s;
}

/** Absolute box in the parent's coordinate space. */
export function box(el: HTMLElement, x: number, y: number, w: number, h: number) {
    const st = el.style;
    st.left = `${x}px`;
    st.top = `${y}px`;
    st.width = `${Math.max(0, w)}px`;
    st.height = `${Math.max(0, h)}px`;
}

/** Box by centre, with a radius capped at half the short side. */
export function cbox(el: HTMLElement, cx: number, cy: number, w: number, h: number, r: number) {
    box(el, cx - w / 2, cy - h / 2, w, h);
    el.style.borderRadius = `${Math.max(0, Math.min(r, w / 2, h / 2))}px`;
}

/** translate + scale with `transform-origin: 0 0`, optionally wrapped by `pre`. */
export function place(el: HTMLElement, x: number, y: number, s = 1, pre = "") {
    el.style.transform = `${pre}translate(${x}px,${y}px) scale(${s})`;
}

/** Deterministic pseudo-random sequence, so scattered layouts are stable across renders. */
export function seeded(seed: number) {
    let s = seed;
    return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

/**
 * Position of `el` relative to `root` from the offset chain — unaffected by
 * any transform a scene has already applied, so it can be re-measured on
 * resize in the middle of a scene. `root` must be positioned.
 */
export function offsetRect(el: HTMLElement, root: HTMLElement) {
    let x = 0;
    let y = 0;
    let n: HTMLElement | null = el;
    while (n && n !== root) {
        x += n.offsetLeft;
        y += n.offsetTop;
        n = n.offsetParent as HTMLElement | null;
    }
    return { x, y, w: el.offsetWidth, h: el.offsetHeight };
}
